import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import swapsApi from '../../api/swaps';
import useAuth from '../../hooks/useAuth';
import useHarnessEnabled from '../../hooks/useHarnessEnabled';
import { isSSLOrAbove } from '../../utils/roles';
import SwapListingCard from './SwapListingCard';
import PostSwapModal from './PostSwapModal';
import SimulateSwapsModal from '../Admin/SimulateSwapsModal';

// ── Tab bar ───────────────────────────────────────────────────────────────────

function Tabs({ tabs, active, onChange }) {
  return (
    <div className="flex gap-1 rounded-xl bg-gray-100 p-1 w-fit">
      {tabs.map(({ key, label, count }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
            active === key
              ? 'bg-white shadow text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {label}
          {count != null && count > 0 && (
            <span className="ml-1.5 rounded-full bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ message }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 bg-white p-12 text-center text-gray-400 text-sm">
      {message}
    </div>
  );
}

// ── Feedback toast ────────────────────────────────────────────────────────────

function Toast({ message, type = 'success', onDismiss }) {
  if (!message) return null;
  return (
    <div className={`flex items-center justify-between rounded-lg border px-4 py-3 text-sm ${
      type === 'success'
        ? 'border-green-200 bg-green-50 text-green-700'
        : 'border-red-200 bg-red-50 text-red-700'
    }`}>
      <span>{message}</span>
      <button onClick={onDismiss} className="ml-4 text-current opacity-50 hover:opacity-100">✕</button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SwapsPage({ approvals = false }) {
  const { user }    = useAuth();
  const queryClient = useQueryClient();
  const isSSL       = isSSLOrAbove(user);

  const [activeTab,    setActiveTab]    = useState(approvals ? 'approvals' : 'open');
  const [showPostModal, setShowPostModal] = useState(false);
  const [swapMode,     setSwapMode]     = useState(null);    // 'posts' | 'claims'
  const [toast,        setToast]        = useState(null);
  const harnessEnabled = useHarnessEnabled();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Data fetching ──────────────────────────────────────────────────────────

  // Open listings — visible to everyone in the district
  const { data: openListings = [], isLoading: openLoading } = useQuery({
    queryKey: ['swaps-open'],
    queryFn:  () => swapsApi.getListings({ status: 'OPEN' }),
  });

  // My posted listings
  const { data: myListings = [], isLoading: myLoading } = useQuery({
    queryKey: ['swaps-mine'],
    queryFn:  () => swapsApi.getListings({ mine: 'posted' }),
    enabled:  !isSSL,
  });

  // Agreed listings — SSL approval queue
  const { data: agreedListings = [], isLoading: agreedLoading } = useQuery({
    queryKey: ['swaps-agreed'],
    queryFn:  () => swapsApi.getListings({ status: 'AGREED' }),
    enabled:  isSSL,
  });

  // All listings — SSL full view
  const { data: allListings = [], isLoading: allLoading } = useQuery({
    queryKey: ['swaps-all'],
    queryFn:  () => swapsApi.getListings({}),
    enabled:  isSSL,
  });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['swaps-open'] });
    queryClient.invalidateQueries({ queryKey: ['swaps-mine'] });
    queryClient.invalidateQueries({ queryKey: ['swaps-agreed'] });
    queryClient.invalidateQueries({ queryKey: ['swaps-all'] });
  };

  const claimMutation = useMutation({
    mutationFn: swapsApi.claimListing,
    onSuccess: () => { invalidateAll(); showToast('Shift claimed — pending SSL approval.'); },
    onError: (err) => showToast(err.message, 'error'),
  });

  const cancelMutation = useMutation({
    mutationFn: swapsApi.cancelListing,
    onSuccess: () => { invalidateAll(); showToast('Listing cancelled.'); },
    onError: (err) => showToast(err.message, 'error'),
  });

  const approveMutation = useMutation({
    mutationFn: swapsApi.approveListing,
    onSuccess: () => { invalidateAll(); showToast('Swap approved successfully.'); },
    onError: (err) => showToast(err.message, 'error'),
  });

  const rejectMutation = useMutation({
    mutationFn: swapsApi.rejectListing,
    onSuccess: () => { invalidateAll(); showToast('Swap rejected — listing reopened.'); },
    onError: (err) => showToast(err.message, 'error'),
  });

  // ── Tab config ─────────────────────────────────────────────────────────────

  const tabs = isSSL
    ? [
        { key: 'approvals', label: 'Approvals',      count: agreedListings.length },
        { key: 'all',       label: 'All Listings',   count: null },
      ]
    : [
        { key: 'open',      label: 'Available',      count: openListings.filter((l) => l.posted_by !== user?.id).length },
        { key: 'mine',      label: 'My Listings',    count: myListings.filter((l) => l.status === 'OPEN').length },
      ];

  // ── Card renderer ──────────────────────────────────────────────────────────

  const renderCard = (listing) => (
    <SwapListingCard
      key={listing.id}
      listing={listing}
      currentUserId={user?.id}
      isSSL={isSSL}
      onClaim={claimMutation.mutate}
      onCancel={cancelMutation.mutate}
      onApprove={approveMutation.mutate}
      onReject={rejectMutation.mutate}
    />
  );

  // ── Active tab content ─────────────────────────────────────────────────────

  const tabContent = () => {
    if (activeTab === 'approvals') {
      if (agreedLoading) return <p className="text-sm text-gray-400 text-center py-8">Loading…</p>;
      if (!agreedListings.length) return <EmptyState message="No swaps awaiting approval." />;
      return <div className="space-y-3">{agreedListings.map(renderCard)}</div>;
    }

    if (activeTab === 'all') {
      if (allLoading) return <p className="text-sm text-gray-400 text-center py-8">Loading…</p>;
      if (!allListings.length) return <EmptyState message="No swap listings found." />;
      return <div className="space-y-3">{allListings.map(renderCard)}</div>;
    }

    if (activeTab === 'open') {
      const available = openListings.filter((l) => l.posted_by !== user?.id);
      if (openLoading) return <p className="text-sm text-gray-400 text-center py-8">Loading…</p>;
      if (!available.length) return <EmptyState message="No shifts available to claim right now." />;
      return <div className="space-y-3">{available.map(renderCard)}</div>;
    }

    if (activeTab === 'mine') {
      if (myLoading) return <p className="text-sm text-gray-400 text-center py-8">Loading…</p>;
      if (!myListings.length) return <EmptyState message="You haven't posted any swap listings." />;
      return <div className="space-y-3">{myListings.map(renderCard)}</div>;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">
          {isSSL ? 'Swap Management' : 'Swap Marketplace'}
        </h1>

        <div className="flex flex-wrap gap-2">
          {/* Test-harness swap sim buttons — only when server is in harness mode */}
          {isSSL && harnessEnabled && (
            <>
              <button
                onClick={() => setSwapMode('posts')}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                Simulate swap posts
              </button>
              <button
                onClick={() => setSwapMode('claims')}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                Simulate swap claims
              </button>
            </>
          )}

          {/* SLs can post a swap */}
          {!isSSL && (
            <button
              onClick={() => setShowPostModal(true)}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
            >
              + Post a Swap
            </button>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-500">
        {isSSL
          ? 'Review agreed swaps and approve or reject them. The shift assignment updates immediately on approval.'
          : 'Claim an available shift swap or post one of your own shifts to the marketplace.'}
      </p>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}

      {/* Tabs */}
      <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {/* Content */}
      {tabContent()}

      {/* Post modal */}
      {showPostModal && (
        <PostSwapModal
          onClose={() => setShowPostModal(false)}
          onPosted={() => showToast('Shift posted to marketplace.')}
        />
      )}

      {/* Test-harness swap simulation modal */}
      {swapMode && (
        <SimulateSwapsModal
          mode={swapMode}
          onClose={() => setSwapMode(null)}
          onDone={(data) => {
            invalidateAll();
            showToast(
              swapMode === 'posts'
                ? `Posted ${data.posted} listings (skipped ${data.skipped_already_listed}).`
                : `Claimed ${data.claimed} listings.`
            );
          }}
        />
      )}
    </div>
  );
}
